// src/api/inventory/shopifyApi.ts
import { ENV } from '../../config/runtimeEnv';
import type {  ShopifyInstallParams, ShopifyInstallResponse, ShopifyCallbackParams, ShopifyCallbackResponse, ShopifyConfig, ShopifyCredentialsPayload, ShopifySyncResult, ShopifyImportProductsParams, ShopifyImportOrdersParams, ShopifyImportQueuedResponse, ShopifyImportStatusResponse, ShopifySyncLog, ShopifyImportStatus, ShopifyWebhookLog } from '../../types/inventory/ShopifyTypes';
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_COMMERCE_SERVER;


export const getShopifyIndex = async () => {
	const response = await http.get(`${API_BASE_URL}/shopify`);
	return response.data;
};

export const initiateShopifyInstall = async (params: ShopifyInstallParams): Promise<ShopifyInstallResponse> => {
	const response = await http.get(`${API_BASE_URL}/shopify/install`, {
		params,
	});
	return response.data;
};

export const handleShopifyCallback = async (params: ShopifyCallbackParams): Promise<ShopifyCallbackResponse> => {
	const response = await http.get(`${API_BASE_URL}/shopify/callback`, {
		params,
	});
	return response.data;
};

export const getShopifyConfig = async (): Promise<ShopifyConfig> => {
	const response = await http.get(`${API_BASE_URL}/shopify/config`);
	return response.data;
};

export const createShopifyConfig = async (data: ShopifyCredentialsPayload): Promise<ShopifyConfig> => {
	const response = await http.post(`${API_BASE_URL}/shopify/config`, data);
	return response.data;
};

export const updateShopifyConfig = async (id: number, data: Partial<ShopifyConfig> & ShopifyCredentialsPayload): Promise<ShopifyConfig> => {
	const response = await http.put(`${API_BASE_URL}/shopify/config/${id}`, data);
	return response.data;
};

export const disconnectShopify = async (): Promise<{ message: string }> => {
	const response = await http.delete(`${API_BASE_URL}/shopify/disconnect`);
	return response.data;
};

export const syncProductsToShopify = async (): Promise<{ message: string; results: ShopifySyncResult }> => {
	const response = await http.post(
		`${API_BASE_URL}/shopify/sync/products`,
		{}
	);
	return response.data;
};

export const importProductsFromShopify = async (params: ShopifyImportProductsParams): Promise<{ message: string; import_only_mode: boolean; note: string; results: ShopifySyncResult }> => {
	const response = await http.post(`${API_BASE_URL}/shopify/import/products`, params);
	return response.data;
};

export const importOrdersFromShopify = async (params: ShopifyImportOrdersParams): Promise<ShopifyImportQueuedResponse> => {
	const response = await http.post(`${API_BASE_URL}/shopify/import/orders`, params);
	return response.data;
};

export const importCustomersFromShopify = async (): Promise<ShopifyImportQueuedResponse> => {
	const response = await http.post(`${API_BASE_URL}/shopify/import/customers`, {});
	return response.data;
};

/**
 * CU-86d42ap9h — a status check is a small, frequent poll, so it carries its own
 * finite deadline: without one a stalled connection holds the tracking chain open
 * indefinitely. Comfortably below the caller's overall tracking window.
 */
export const SHOPIFY_IMPORT_STATUS_TIMEOUT_MS = 10_000;

export const getShopifyImportStatus = async (jobId: string, options?: { signal?: AbortSignal }): Promise<ShopifyImportStatusResponse> => {
	const response = await http.get(`${API_BASE_URL}/shopify/import/status/${jobId}`, {
		timeout: SHOPIFY_IMPORT_STATUS_TIMEOUT_MS,
		signal: options?.signal,
	});
	return response.data;
};

export const getShopifySyncStatus = async (shop_domain: string, limit: number = 50): Promise<{ logs: ShopifySyncLog[] }> => {
	const response = await http.get(`${API_BASE_URL}/shopify/sync/status`, {
		params: { shop_domain, limit },
	});
	return response.data;
};

export const getCustomerSyncStatus = async (shop_domain: string, limit: number = 50): Promise<{ logs: ShopifySyncLog[] }> => {
	const response = await http.get(`${API_BASE_URL}/shopify/customers/sync-status`, {
		params: { shop_domain, limit },
	});
	return response.data;
};

export const getShopifyWebhookLogs = async (shop_domain: string, topic?: string, processed?: boolean, limit: number = 50): Promise<{ webhooks: ShopifyWebhookLog[] }> => {
	const response = await http.get(`${API_BASE_URL}/shopify/webhooks/logs`, {
		params: { shop_domain, topic, processed, limit },
	});
	return response.data;
};

export const retryShopifyWebhook = async (webhook_id?: number, shop_domain?: string): Promise<{ message: string; webhook_id?: number; results?: any }> => {
	const response = await http.post(
		`${API_BASE_URL}/shopify/webhooks/retry`,
		{ webhook_id, shop_domain }
	);
	return response.data;
};

export const cleanupShopifyWebhooks = async (days: number = 30): Promise<{ message: string; deleted_count: number }> => {
	const response = await http.delete(`${API_BASE_URL}/shopify/webhooks/cleanup`, {
		params: { days },
	});
	return response.data;
};